"use client";

import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  MoreHorizontal,
  ArrowUpDown,
  Eye,
  CheckCircle,
  XCircle,
  Trash2,
  Sparkles,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Spinner } from "../components/ui/shadcn-io/spinner";

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Leave type definition
export type Leave = {
  id: string; // Mapped from _id
  _id: string;
  employee?: {
    _id: string;
    fullName: string;
    email: string;
    employeeType: string;
  };
  employeeName?: string;
  employeeEmail?: string;
  employeeType?: string;
  company?: string;
  leaveMode?: "regular" | "comp-off";
  compOffId?: string;
  compOff?: {
    _id?: string;
    workDate?: string;
    creditDays?: number;
    expiryDate?: string;
    status?: string;
  };
  leaveType: string;
  startDate: string;
  endDate: string;
  numberOfDays: number;
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  appliedDate: string;
  rejectionReason?: string;
  isPaidLeave: boolean;
};

// Status badge helper
const getStatusBadge = (status: string) => {
  switch (status) {
    case "Approved":
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
          <CheckCircle className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    case "Rejected":
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">
          <XCircle className="mr-1 h-3 w-3" />
          Rejected
        </Badge>
      );
    case "Pending":
    default:
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">
          <span className="mr-1">⏳</span>
          Pending
        </Badge>
      );
  }
};

const isCompOffLeave = (leave: Leave) =>
  leave.leaveMode === "comp-off" || !!leave.compOffId;

// Leave type badge helper (paid/unpaid vs comp-off)
const getLeaveTypeBadge = (leave: Leave) => {
  if (isCompOffLeave(leave)) {
    return (
      <Badge className="bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100 flex items-center gap-1">
        <Sparkles className="h-3 w-3" />
        Comp-Off
      </Badge>
    );
  }

  return leave.isPaidLeave ? (
    <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">
      Paid
    </Badge>
  ) : (
    <Badge className="bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-100">
      Unpaid
    </Badge>
  );
};

// Actions cell for admin
const AdminActionsCell = ({
  leave,
  onApprove,
  onReject,
  onView,
}: {
  leave: Leave;
  onApprove: (leave: Leave) => void;
  onReject: (leave: Leave) => void;
  onView: (leave: Leave) => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onView(leave)}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        {leave.status === "Pending" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onApprove(leave)}
              className="text-green-600"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onReject(leave)}
              className="text-red-600"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Actions cell for employee
const EmployeeActionsCell = ({
  leave,
  onCancel,
  onView,
}: {
  leave: Leave;
  onCancel: (leave: Leave) => void;
  onView: (leave: Leave) => void;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onView(leave)}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        {leave.status === "Pending" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onCancel(leave)}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Cancel Leave
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Admin columns
export const createAdminLeaveColumns = (
  onApprove: (leave: Leave) => void,
  onReject: (leave: Leave) => void,
  onView: (leave: Leave) => void
): ColumnDef<Leave>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "leaveMode",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Mode
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const mode =
        (row.getValue("leaveMode") as string) ||
        (row.original.compOffId ? "comp-off" : "regular");
      const isComp = mode === "comp-off";
      return (
        <div className="text-left">
          <Badge
            className={
              isComp
                ? "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100"
                : "bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-100"
            }
          >
            {isComp ? "Comp-Off" : "Regular"}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "employeeName",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name =
        row.original.employee?.fullName || row.original.employeeName || "N/A";
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  // {
  //   accessorKey: "employeeEmail",
  //   header: ({ column }) => {
  //     return (
  //       <Button
  //         variant="ghost"
  //         onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  //         className="h-auto p-0 justify-start"
  //       >
  //         Email
  //         <ArrowUpDown className="ml-2 h-4 w-4" />
  //       </Button>
  //     );
  //   },
  //   cell: ({ row }) => {
  //     const email =
  //       row.original.employee?.email || row.original.employeeEmail || "N/A";
  //     return <div className="text-muted-foreground text-left">{email}</div>;
  //   },
  // },
  {
    accessorKey: "leaveType",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Leave Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const leaveType = row.getValue("leaveType") as string;
      const isComp = isCompOffLeave(row.original);
      return (
        <div className="text-left">
          {leaveType}
          {isComp && (
            <p className="text-xs text-muted-foreground">
              {row.original.compOff?.workDate
                ? `From ${formatDate(row.original.compOff.workDate)}`
                : "Comp-Off credit"}
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "startDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Start Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("startDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    accessorKey: "endDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          End Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("endDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    accessorKey: "numberOfDays",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Days
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const days = row.getValue("numberOfDays") as number;
      return <div className="text-left font-medium">{days}</div>;
    },
  },
  {
    accessorKey: "isPaidLeave",
    header: "Type",
    cell: ({ row }) => {
      return <div className="text-left">{getLeaveTypeBadge(row.original)}</div>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return <div className="text-left">{getStatusBadge(status)}</div>;
    },
  },
  {
    accessorKey: "appliedDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Applied Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("appliedDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      return (
        <AdminActionsCell
          leave={row.original}
          onApprove={onApprove}
          onReject={onReject}
          onView={onView}
        />
      );
    },
  },
];

// Employee columns
export const createEmployeeLeaveColumns = (
  onCancel: (leave: Leave) => void,
  onView: (leave: Leave) => void
): ColumnDef<Leave>[] => [
  {
    accessorKey: "leaveMode",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="h-auto p-0 justify-start"
      >
        Mode
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const mode =
        (row.getValue("leaveMode") as string) ||
        (row.original.compOffId ? "comp-off" : "regular");
      const isComp = mode === "comp-off";
      return (
        <div className="text-left">
          <Badge
            className={
              isComp
                ? "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100"
                : "bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-100"
            }
          >
            {isComp ? "Comp-Off" : "Regular"}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "leaveType",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Leave Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const leaveType = row.getValue("leaveType") as string;
      const isComp = isCompOffLeave(row.original);
      return (
        <div className="text-left font-medium">
          {leaveType}
          {isComp && row.original.compOff?.workDate && (
            <p className="text-xs text-muted-foreground">
              Earned on {formatDate(row.original.compOff.workDate)}
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "startDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Start Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("startDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    accessorKey: "endDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          End Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("endDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    accessorKey: "numberOfDays",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Days
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const days = row.getValue("numberOfDays") as number;
      return <div className="text-left font-medium">{days}</div>;
    },
  },
  {
    accessorKey: "isPaidLeave",
    header: "Type",
    cell: ({ row }) => {
      return <div className="text-left">{getLeaveTypeBadge(row.original)}</div>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return <div className="text-left">{getStatusBadge(status)}</div>;
    },
  },
  {
    accessorKey: "appliedDate",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Applied Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("appliedDate") as string;
      return <div className="text-left">{formatDate(dateString)}</div>;
    },
  },
  {
    accessorKey: "reason",
    header: "Reason",
    cell: ({ row }) => {
      const reason = row.getValue("reason") as string;
      return (
        <div className="text-left max-w-[200px] truncate" title={reason}>
          {reason}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      return (
        <EmployeeActionsCell
          leave={row.original}
          onCancel={onCancel}
          onView={onView}
        />
      );
    },
  },
];
